import { ChangeDetectorRef, Component, effect, inject, Input, OnChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { PopoverController } from '@ionic/angular';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { fromEvent, startWith, map } from 'rxjs';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';

@Component({
    standalone: false,
    selector: 'abs-menu',
    template: `
        <ion-list *ngIf="style === 'list'">
            <ng-container *ngFor="let item of menuItems; let i = index">
                @if (!item.hide) {
                    <ion-item-divider *ngIf="item.children" (click)="showMenu($event, item.children)">
                        {{ item.title | translate }}
                    </ion-item-divider>
                    <abs-menu
                        *ngIf="layout === 'vertical' && item.children"
                        [isLoggedIn]="isLoggedIn"
                        [roles]="roles"
                        [menuItems]="item.children"
                        [mode]="mode"
                        [isChild]="true"
                    >
                    </abs-menu>
                    <ion-menu-toggle auto-hide="false" *ngIf="!item.children && mode === 'root'">
                        <ng-template [ngTemplateOutlet]="menuTemplate"></ng-template>
                    </ion-menu-toggle>

                    <ng-container *ngIf="mode !== 'root'">
                        <ng-template [ngTemplateOutlet]="menuTemplate"></ng-template>
                    </ng-container>

                    <ng-template #menuTemplate>
                        <ion-item
                            *ngIf="item.url && !isExternalUrl(item.url)"
                            [class.alt]="i % 2 === 0"
                            [routerDirection]="mode"
                            [routerLink]="[item.url]"
                            lines="full"
                        >
                            <ion-icon slot="start" [name]="item.icon"></ion-icon>
                            <ion-label *ngIf="isChild || layout === 'vertical'">
                                {{ item.title | translate }}
                            </ion-label>
                        </ion-item>

                        <ion-item
                            *ngIf="item.url && isExternalUrl(item.url)"
                            [class.alt]="i % 2 === 0"
                            [button]="true"
                            (click)="followLink(item)"
                            lines="full"
                        >
                            <ion-icon slot="start" [name]="item.icon"></ion-icon>
                            <ion-label *ngIf="isChild || layout === 'vertical'">
                                {{ item.title | translate }}
                            </ion-label>
                        </ion-item>

                        <ion-item
                            *ngIf="item.route && !item.url"
                            [class.alt]="i % 2 === 0"
                            [routerDirection]="mode"
                            [button]="true"
                            (click)="followLink(item)"
                            lines="full"
                        >
                            <ion-icon slot="start" [name]="item.icon"></ion-icon>
                            <ion-label *ngIf="isChild || layout === 'vertical'">
                                {{ item.title | translate }}
                            </ion-label>
                        </ion-item>

                        <ion-item
                            *ngIf="item.onClick"
                            button="true"
                            [class.alt]="i % 2 === 0"
                            [routerDirection]="mode"
                            (click)="item.onClick()"
                            lines="full"
                        >
                            <ion-icon slot="start" [name]="item.icon"></ion-icon>
                            <ion-label *ngIf="isChild || layout === 'vertical'">
                                {{ item.title | translate }}
                            </ion-label>
                        </ion-item>
                    </ng-template>
                }
            </ng-container>
        </ion-list>

        <ul class="topMenu" [class]="layout" *ngIf="style === 'menu'">
            <ng-container *ngFor="let item of menuItems; let i = index">
                <li *ngIf="!item.hide" class="ion-activatable" [class.placeholder]="item.placeholder">
                    <a *ngIf="item.children" [routerLink]="[item.children[0].url]">
                        <span>{{ item.title | translate }}</span>
                    </a>
                    <a *ngIf="item.url && !isExternalUrl(item.url)" [routerLink]="[item.url]">
                        <span><ion-icon [name]="item.icon"></ion-icon> {{ item.title | translate }}</span>
                    </a>
                    <a *ngIf="item.url && isExternalUrl(item.url)" (click)="followLink(item)">
                        <span><ion-icon [name]="item.icon"></ion-icon> {{ item.title | translate }}</span>
                    </a>
                    <a *ngIf="item.route && !item.url" (click)="followLink(item)">
                        <span><ion-icon [name]="item.icon"></ion-icon> {{ item.title | translate }}</span>
                    </a>
                    <a *ngIf="item.onClick" (click)="item.onClick()">
                        <span><ion-icon [name]="item.icon"></ion-icon> {{ item.title | translate }}</span>
                    </a>

                    <abs-menu
                        *ngIf="item.children"
                        [style]="style"
                        [isLoggedIn]="isLoggedIn"
                        [roles]="roles"
                        [menuItems]="item.children"
                        layout="vertical"
                    ></abs-menu>
                </li>
            </ng-container>
        </ul>
    `,
    styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnChanges {
    popoverController = inject(PopoverController);
    private featureFlagService = inject(FeatureFlagService);
    private cdr = inject(ChangeDetectorRef);

    @Input()
    menuItems: MenuItem[];

    @Input()
    isLoggedIn = false;

    @Input()
    roles: string[] = [];

    @Input()
    isChild = false;

    @Input()
    mode: 'forward' | 'root' = 'forward';

    @Input()
    layout: 'vertical' | 'horizontal' = 'vertical';

    @Input()
    style: 'menu' | 'list' = 'list';

    windowSize = toSignal(
        fromEvent(window, 'resize').pipe(
            startWith(window.innerWidth),
            map(() => window.innerWidth)
        )
    );

    constructor(
        private iab: InAppBrowser,
        private router: Router,
        private route: ActivatedRoute
    ) {
        effect(() => {
            this.featureFlagService.flags();
            this.applyFeatureFlags();
            this.cdr.markForCheck();
        });
    }

    isExternalUrl(url: string): boolean {
        return url?.startsWith('http');
    }

    ngOnChanges() {
        this.menuItems = this.menuItems.filter((item) => {
            let hide = false;
            if (item.requiresLogin && !this.isLoggedIn) {
                hide = true;
            }

            if (item.requiredRoles && item.requiredRoles.length > 0) {
                hide = !this.roles.some((role) => item.requiredRoles.includes(role));
            }

            return !hide;
        });
        if (this.layout === 'vertical') {
            this.menuItems = this.menuItems.filter((x) => x.placeholder !== true);
        }
        this.applyFeatureFlags();
    }

    private applyFeatureFlags() {
        if (!this.menuItems) return;
        for (const item of this.menuItems) {
            if (item.requiredFeature) {
                item.hide = !this.featureFlagService.isFeatureVisible(item.requiredFeature)();
            }
        }
    }

    followLink(item: MenuItem) {
        if (item?.url?.startsWith('http')) {
            this.iab.create(item.url);
        } else if (item.route) {
            this.router.navigate([item.route], { relativeTo: this.route });
        }
    }

    async showMenu(ev: any, menuItems: MenuItem[]) {
        if (this.layout === 'vertical') {
            return;
        }
        const popover = await this.popoverController.create({
            component: MenuComponent,
            componentProps: {
                menuItems,
                isLoggedIn: this.isLoggedIn,
                roles: this.roles,
                mode: this.mode,
                layout: 'vertical'
            },
            event: ev,
            translucent: true
        });
        return await popover.present();
    }
}

export interface MenuItem {
    title: string;
    icon?: string;
    url?: string;
    onClick?: () => void;
    children?: MenuItem[];
    requiresLogin?: boolean;
    requiredRoles?: string[];
    requiredFeature?: string;
    route?: string;
    placeholder?: boolean;
    hide?: boolean;
}
